import { canManageCases, fmtDayLongYear, fmtDayShort, fmtTime } from '@concord/domain';
import { Button, Icon, Notice, PageHead, PanelHead, PanelMore, PhaseGate, PhaseTag } from '@concord/ui';
import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { useNavigate } from 'react-router';
import { api, errorMessage } from '../../lib/api';
import type { Me } from '../../lib/session';
import { QueueChips } from '../cases/CasesPage';
import { type CaseRowView, CaseTable, Legend, type Queue } from '../cases/CaseTable';
import s from '../cases/cases.module.css';
import { ConsoleLayout } from './ConsoleLayout';
import { NewCaseDialog } from './NewCase';

interface Summary {
  tiles: {
    attention: { count: number; detail: string };
    week: { count: number; next: string | null };
    month: { count: number; spark: number[] };
  };
  counts: Record<Queue, number>;
  attention: CaseRowView[];
  attentionTotal: number;
  allTotal: number;
  consultationsWeek: {
    caseRef: string;
    startsAt: string;
    patient: string;
    detail: string;
    status: string;
    soon: boolean;
  }[];
  decisions: { tone: 'crit' | 'warn'; title: string; detail: string; caseRef: string | null }[];
}

export function partOfDay(d = new Date()): 'morning' | 'afternoon' | 'evening' {
  const h = d.getHours();
  return h < 12 ? 'morning' : h < 18 ? 'afternoon' : 'evening';
}

/** The navigator's dashboard, ported from concord-dashboard.html. Later-phase tiles are marked. */
export function ConsoleHome({ me }: { me: Me }) {
  const navigate = useNavigate();
  const [creating, setCreating] = useState(false);
  const caseAccess = canManageCases(me.permissions);
  const q = useQuery({
    queryKey: ['dashboard'],
    queryFn: () => api<Summary>('/staff/dashboard'),
    refetchInterval: 60_000,
    enabled: caseAccess,
  });
  const now = new Date();
  const tz = me.timezone;
  const d = q.data;
  const next = d?.tiles.week.next ? new Date(d.tiles.week.next) : null;
  const minutes = next ? Math.round((next.getTime() - now.getTime()) / 60_000) : null;
  const max = Math.max(1, ...(d?.tiles.month.spark ?? [1]));
  // Seven weekly bars, oldest first; ids name the week so React keys are stable.
  const weeks = (d?.tiles.month.spark ?? []).map((n, idx, all) => ({
    n,
    id: `weeks-ago-${all.length - 1 - idx}`,
    current: idx === all.length - 1,
  }));

  return (
    <ConsoleLayout me={me} current="dashboard" roomy>
      <PageHead
        roomy
        title={`Good ${partOfDay(now)}, ${me.firstName}`}
        when={`${fmtDayLongYear(now, tz)}${d ? ` · ${d.tiles.attention.count} ${d.tiles.attention.count === 1 ? 'case needs' : 'cases need'} you today` : ''}`}
        actions={
          <Button onClick={() => setCreating(true)}>
            <Icon.Plus />
            New case
          </Button>
        }
      />
      {!caseAccess && (
        <Notice>Your account does not include case access. Ask a super admin if you need it.</Notice>
      )}
      {q.isError && <Notice tone="bad">{errorMessage(q.error)}</Notice>}
      {d && (
        <>
          <section className={s.tiles} aria-label="Today at a glance">
            <div className={`${s.tile} ${s.hold}`}>
              <span className="key">Needs attention</span>
              <span className={s.v}>{d.tiles.attention.count}</span>
              <span className={s.m}>{d.tiles.attention.detail}</span>
            </div>
            <div className={s.tile}>
              <span className="key">Consultations this week</span>
              <span className={s.v}>{d.tiles.week.count}</span>
              <span className={s.m}>
                {next
                  ? minutes !== null && minutes >= 0 && minutes <= 180
                    ? `Next at ${fmtTime(next, tz)} — in ${minutes} min`
                    : `Next ${fmtDayShort(next, tz)}, ${fmtTime(next, tz)}`
                  : 'None booked'}
              </span>
            </div>
            <PhaseGate phase="P3" tag={false}>
              <div className={s.tile} aria-disabled="true" style={{ height: '100%' }}>
                <span className="key" style={{ display: 'flex', gap: '.4rem', alignItems: 'center' }}>
                  Awaiting minutes <PhaseTag phase="P3" />
                </span>
                <span className={s.v}>—</span>
                <span className={s.m}>Consultation minutes and physician earnings arrive in Phase 3</span>
              </div>
            </PhaseGate>
            <div className={s.tile}>
              <span className="key">Cases this month</span>
              <span className={s.v}>{d.tiles.month.count}</span>
              <div
                className={s.spark}
                role="img"
                aria-label={`Cases submitted per week over the last seven weeks: ${d.tiles.month.spark.join(', ')}. This week is the final bar.`}
              >
                {weeks.map((b) => (
                  <i
                    key={b.id}
                    className={b.current ? s.now : undefined}
                    style={{ height: `${Math.max(10, (b.n / max) * 100)}%` }}
                  />
                ))}
              </div>
            </div>
          </section>

          <QueueChips
            counts={d.counts}
            current={'attention' as Queue}
            onPick={(k) => navigate(`/console/cases?queue=${k}`)}
          />

          <div className={s.split}>
            <section className="panel" aria-label="Cases needing attention">
              <PanelHead
                title="Cases needing attention"
                sub={`${d.attentionTotal} of ${d.allTotal.toLocaleString('en-US')}`}
                more={
                  <PanelMore onClick={() => navigate('/console/cases?queue=attention')}>
                    Open case list →
                  </PanelMore>
                }
              />
              {d.attention.length ? (
                <CaseTable rows={d.attention} tz={tz} now={now} />
              ) : (
                <div className={s.empty}>Nothing needs you right now.</div>
              )}
              <Legend />
            </section>

            <div className={s.side}>
              <section className="panel" aria-label="This week's consultations">
                <PanelHead title="This week's consultations" sub={d.consultationsWeek.length} />
                {d.consultationsWeek.length === 0 && <div className={s.empty}>None booked this week.</div>}
                {d.consultationsWeek.map((c) => (
                  // biome-ignore lint/a11y/useSemanticElements: a row that opens the case, styled as the prototype's slot
                  <div
                    key={`${c.caseRef}${c.startsAt}`}
                    className={s.slot}
                    role="link"
                    tabIndex={0}
                    onClick={() => navigate(`/console/cases/${c.caseRef}`)}
                    onKeyDown={(e) => e.key === 'Enter' && navigate(`/console/cases/${c.caseRef}`)}
                  >
                    <span className={s.t}>
                      {fmtDayShort(new Date(c.startsAt), tz).split(',')[0]}{' '}
                      {fmtTime(new Date(c.startsAt), tz).replace(' AM', '').replace(' PM', '')}
                    </span>
                    <div className={s.b}>
                      <div className={s.n1}>{c.patient}</div>
                      <div className={s.n2}>{c.detail}</div>
                    </div>
                    <span className={`${s.st} ${c.soon ? s['st-soon'] : s['st-none']}`}>{c.status}</span>
                  </div>
                ))}
              </section>

              <section className="panel" aria-label="Needs a decision">
                <PanelHead title="Needs a decision" />
                {d.decisions.map((x) => (
                  <div key={x.title} className={`${s.alert} ${x.tone === 'crit' ? s.crit : s.warn}`}>
                    <span className={s.bar} />
                    <div>
                      <div className={s.h}>{x.title}</div>
                      <div className={s.d}>{x.detail}</div>
                    </div>
                  </div>
                ))}
                <div className={`${s.alert} ${s.warn}`} style={{ opacity: 0.7 }}>
                  <span className={s.bar} style={{ background: 'var(--neutral-mark)' }} />
                  <div>
                    <div className={s.h}>
                      Coverage monitoring <PhaseTag phase="P5" />
                    </div>
                    <div className={s.d}>
                      Thin networks are surfaced here before a case runs out of candidates.
                    </div>
                  </div>
                </div>
              </section>

              <PhaseGate phase="P3" tag={false}>
                <section className="panel" aria-label="Payables">
                  <PanelHead title="Payables" sub="This period">
                    <PhaseTag phase="P3" />
                  </PanelHead>
                  <div className={s.money}>
                    <div className={s.mrow}>
                      <span className={s.l}>Posted, earned</span>
                      <span className={s.r}>—</span>
                    </div>
                    <div className={s.mrow}>
                      <span className={s.l}>Awaiting minutes</span>
                      <span className={s.r}>—</span>
                    </div>
                    <div className={s.mnote}>
                      Minutes and physician earnings arrive in Phase 3. Payment is made by CEN outside the
                      platform.
                    </div>
                  </div>
                </section>
              </PhaseGate>
            </div>
          </div>
        </>
      )}
      <NewCaseDialog open={creating} onOpenChange={setCreating} />
    </ConsoleLayout>
  );
}
